export interface NotificationAttrs {
  user_id?: string;
  actor_user_id?: string;
  actor_name?: string | null;
  actor_email?: string | null;
  workspace_id?: string;
  type?: string;
  subject_type?: string;
  subject_id?: string;
  metadata?: Record<string, unknown>;
  read_at?: string | null;
  created_at?: string | null;
}

export interface NotificationItem {
  id: string;
  attributes?: NotificationAttrs;
}

export interface FormattedNotification {
  /** Verb phrase — caller composes "<actor> {verb}". */
  verb: string;
  /** Optional inline subject reference (clickable when present). */
  parentLabel: string | null;
  /** Optional plain-text excerpt of the surrounding body, ~140 chars. */
  excerpt: string | null;
  /**
   * The route the user lands on when they click. Built from
   * `metadata.workspace_slug` (snapshotted at fan-out time) plus the
   * subject context — never re-derived from the live workspace
   * mapping.
   */
  href: NotificationHref;
}

export type NotificationHref =
  | { kind: 'task'; workspaceSlug: string; taskUlid: string }
  | { kind: 'note'; workspaceSlug: string; noteUlid: string }
  /**
   * Calendar event notifications route to the calendar surface where
   * the event lives — the project calendar when `projectSlug` is set,
   * the workspace home calendar widget otherwise. Events don't have a
   * dedicated detail page in v1; the user opens the dialog from the
   * calendar surface.
   */
  | { kind: 'calendar-event'; workspaceSlug: string; projectSlug: string | null }
  | { kind: 'none' };

const stringFromMeta = (
  attrs: NotificationAttrs,
  key: string,
): string | null => {
  const v = attrs.metadata?.[key];
  return typeof v === 'string' ? v : null;
};

/**
 * Translate a notification row into the verb-phrase + subject-ref
 * shape the bell dropdown renders. v1 has only the `mention` type;
 * branches for future types fall through to a generic verb.
 *
 * Deep-link routing: the API stamps `metadata.deep_link_url` at
 * fan-out time, but it's an absolute URL meant for emails. The SPA
 * builds its own internal route from `metadata.parent_kind` +
 * `parent_id` so TanStack Router can navigate without a full page
 * load.
 */
export const formatNotification = (
  item: NotificationItem,
  workspaceSlugById: ReadonlyMap<string, string>,
): FormattedNotification => {
  const attrs = item.attributes ?? {};
  const type = attrs.type ?? '';
  const subjectType = attrs.subject_type;
  const parentKind = stringFromMeta(attrs, 'parent_kind');
  const parentId = stringFromMeta(attrs, 'parent_id');
  const parentLabel = stringFromMeta(attrs, 'parent_label');
  const excerpt = stringFromMeta(attrs, 'excerpt');

  const wsSlug = attrs.workspace_id
    ? workspaceSlugById.get(attrs.workspace_id) ?? null
    : null;

  if (type === 'mention') {
    const verb = subjectType === 'note'
      ? 'mentioned you in a note'
      : 'mentioned you in a comment';

    return {
      verb,
      parentLabel,
      excerpt,
      href: buildHref(subjectType, attrs.subject_id, parentKind, parentId, wsSlug),
    };
  }

  if (type === 'event.invited' || type === 'event.updated' || type === 'event.cancelled') {
    const verb =
      type === 'event.invited'
        ? 'invited you to'
        : type === 'event.updated'
          ? 'updated event'
          : 'cancelled event';
    // Backend snapshots `workspace_slug` + `project_slug` directly
    // into metadata so routing doesn't need the workspaceId map.
    const eventWsSlug = stringFromMeta(attrs, 'workspace_slug') ?? wsSlug;
    const projectSlug = stringFromMeta(attrs, 'project_slug');
    return {
      verb,
      parentLabel,
      excerpt: null, // events don't carry an excerpt; the title is the parentLabel
      href: eventWsSlug
        ? { kind: 'calendar-event', workspaceSlug: eventWsSlug, projectSlug }
        : { kind: 'none' },
    };
  }

  // Forward-compat — render unknown types as a neutral phrase
  // rather than blowing up an inbox row.
  return { verb: 'sent you a notification', parentLabel, excerpt, href: { kind: 'none' } };
};

/**
 * Routing rules:
 *  - Comment mention → navigate to the comment's parent (task/note)
 *    page; the comment thread loads alongside.
 *  - Note-body mention → navigate to the note itself (subject_id is
 *    the note ULID).
 */
const buildHref = (
  subjectType: string | undefined,
  subjectId: string | undefined,
  parentKind: string | null,
  parentId: string | null,
  workspaceSlug: string | null,
): NotificationHref => {
  if (!workspaceSlug) return { kind: 'none' };

  if (subjectType === 'note' && subjectId) {
    return { kind: 'note', workspaceSlug, noteUlid: subjectId };
  }

  // Comment mentions hop to the parent.
  if (subjectType === 'comment' && parentId) {
    if (parentKind === 'task') {
      return { kind: 'task', workspaceSlug, taskUlid: parentId };
    }
    if (parentKind === 'note') {
      return { kind: 'note', workspaceSlug, noteUlid: parentId };
    }
  }

  return { kind: 'none' };
};
